import type { ReactNode } from "react";
import { Navigate, useLocation } from "react-router";

import { canViewAdmin, isReadOnly } from "@/lib/auth/access";
import { useAuth } from "@/lib/auth/AuthProvider";
import { Skeleton } from "@/components/ui/Skeleton";

/** Where an account of each status is allowed to be. */
const STATUS_HOME: Record<string, string> = {
  draft: "/onboarding",
  pending: "/pending",
  rejected: "/rejected",
  disabled: "/login",
};

/** Where somebody signed in with a temporary password is held. */
const CHOOSE_PASSWORD = "/choose-password";

function BootScreen() {
  return (
    <div className="flex min-h-dvh items-center justify-center p-6">
      <div className="w-full max-w-sm space-y-3">
        <Skeleton className="h-4 w-32" />
        <Skeleton className="h-9 w-full" />
        <Skeleton className="h-9 w-full" />
      </div>
    </div>
  );
}

export function RequireAuth({ children }: { children: ReactNode }) {
  const { session, initialising } = useAuth();
  const location = useLocation();

  if (initialising) return <BootScreen />;
  if (!session) {
    return <Navigate to="/login" replace state={{ from: location.pathname }} />;
  }
  // Signed in with a password HR gave out: the server refuses everything
  // else until it is replaced, so nothing else is shown either.
  if (session.must_change_password && location.pathname !== CHOOSE_PASSWORD) {
    return <Navigate to={CHOOSE_PASSWORD} replace />;
  }
  return <>{children}</>;
}

/**
 * Keeps an account on the page its status allows.
 *
 * A draft account belongs in onboarding, a pending one on the waiting page.
 * Without this an employee could bookmark /leaves and hit a wall of 403s.
 */
export function RequireActive({ children }: { children: ReactNode }) {
  const { session } = useAuth();
  if (!session) return null;

  const redirect = STATUS_HOME[session.user.status];
  if (redirect) return <Navigate to={redirect} replace />;
  return <>{children}</>;
}

/**
 * Screens that exist only to write something: an approver, who reads HR's
 * screens but changes nothing, is taken to the list they belong to instead.
 */
const WRITING_ONLY: readonly [RegExp, string][] = [
  [/^\/admin\/documents\/write(\/|$)/, "/admin/documents"],
  [/^\/admin\/documents\/[^/]+\/build$/, "/admin/documents"],
];

/** HR, or an approver reading HR's screens. */
export function RequireAdmin({ children }: { children: ReactNode }) {
  const { session } = useAuth();
  const location = useLocation();
  if (!session) return null;
  if (!canViewAdmin(session)) return <Navigate to="/home" replace />;
  if (isReadOnly(session)) {
    const away = WRITING_ONLY.find(([pattern]) => pattern.test(location.pathname));
    if (away) return <Navigate to={away[1]} replace />;
  }
  return <>{children}</>;
}

/**
 * The salary sheets and pay changes in the employee app, for the employees
 * who check and file them: Accounts, the WPS officer and the Accounting
 * Manager. Anybody else who follows an old link goes home rather than to a
 * refusal.
 */
export function RequirePayrollChain({ children }: { children: ReactNode }) {
  const { session } = useAuth();
  if (!session) return null;
  if (!session.in_payroll_chain && session.user.role !== "admin") {
    return <Navigate to="/home" replace />;
  }
  return <>{children}</>;
}

/** Sends a signed-in visitor away from the sign-in and register pages. */
export function RedirectIfSignedIn({ children }: { children: ReactNode }) {
  const { session, initialising } = useAuth();
  if (initialising) return <BootScreen />;
  if (session) {
    if (session.must_change_password) return <Navigate to={CHOOSE_PASSWORD} replace />;
    const redirect = STATUS_HOME[session.user.status];
    if (redirect && redirect !== "/login") return <Navigate to={redirect} replace />;
    return <Navigate to={canViewAdmin(session) ? "/admin" : "/home"} replace />;
  }
  return <>{children}</>;
}
